import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Worker, Viewer, ScrollMode, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import Accordion from '../components/Accordion';
import Footer from '../components/Footer';
import { getCommittee, type CommitteeMember } from '../features/committee';

const CONSTITUTION_PDF_URL = `${process.env.PUBLIC_URL || ''}/constitution.pdf`;
const PDF_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js';

const AboutPage: React.FC = () => {
  const [committeeMembers, setCommitteeMembers] = useState<CommitteeMember[]>([]);
  const [pdfDefaultScale, setPdfDefaultScale] = useState<number | SpecialZoomLevel | undefined>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? SpecialZoomLevel.PageWidth : undefined
  );

  useEffect(() => {
    getCommittee().then(setCommitteeMembers).catch(console.error);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setPdfDefaultScale(mq.matches ? SpecialZoomLevel.PageWidth : undefined);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const byType = useMemo(() => {
    const committee: CommitteeMember[] = [];
    const trustees: CommitteeMember[] = [];
    const imaamat: CommitteeMember[] = [];
    const admin: CommitteeMember[] = [];
    const staff: CommitteeMember[] = [];
    committeeMembers.forEach((m) => {
      const type = m.memberType || 'Committee';
      if (type === 'Trustee') trustees.push(m);
      else if (type === 'Imaamat') imaamat.push(m);
      else if (type === 'Admin') admin.push(m);
      else if (type === 'Staff') staff.push(m);
      else committee.push(m);
    });
    return { committee, trustees, imaamat, admin, staff };
  }, [committeeMembers]);

  const renderMemberCard = (member: CommitteeMember) => (
    <div key={member.id} className="col-md-6 col-lg-4">
      <div className="card h-100 shadow-sm about-committee-card border-0">
        <div className="card-body d-flex flex-column align-items-center text-center">
          <h5 className="about-committee-card__name mb-1">
            {member.firstName} {member.lastName}
          </h5>
          {member.description && (
            <p className="about-committee-card__description mb-0">{member.description}</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <main className="min-vh-100">
        <div className="container my-4 my-md-5 py-4 px-3 px-md-0">
          <section id="about" className="mb-5">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center mb-5">
                <span className="section-eyebrow">About</span>
                <h1 className="display-5 fw-bold text-primary mb-4 mt-2">Empowering Community</h1>
                <p className="lead mb-3">
                  Welcome to Bothasig Muslim Cultural Society. We are a vibrant community dedicated to celebrating our culture, traditions, and values. Our mission is to enrich lives through unity, support, and shared experiences.
                </p>
                <p className="text-muted mb-0">
                  At Bothasig, we believe in the power of coming together. Our events are filled with joy, learning, and connection. Whether through workshops, festivals, or community service, everyone is welcome.
                </p>
              </div>
            </div>
          </section>

          {(byType.imaamat.length > 0 || byType.committee.length > 0 || byType.admin.length > 0 || byType.trustees.length > 0 || byType.staff.length > 0) && (
            <section id="ourcommittee" className="mb-5">
              {byType.imaamat.length > 0 && (
                <div className="mb-5">
                  <h2 className="text-center mb-2 section-subtitle">Imaamat</h2>
                  <p className="text-center text-muted mb-4 mx-auto" style={{ maxWidth: '60ch' }}>
                    The Imaamat is a dedicated religious and community service body based at the Bothasig Musallah,
                    serving the surrounding areas. The Panel is committed to uplifting the Muslim community through
                    accessible, confidential and free services.
                  </p>
                  <div className="row g-4 justify-content-center">
                    {byType.imaamat.map(renderMemberCard)}
                  </div>
                  <div className="row mt-4">
                    <div className="col-lg-10 mx-auto">
                      <Accordion
                        defaultOpenId="purpose"
                        items={[
                          {
                            id: 'purpose',
                            title: 'Purpose',
                            content: (
                              <p className="text-muted mb-0">
                                Our primary aim is to provide religious guidance, emotional support, and community services that
                                align with the Quraan and Sunnah. We strive to foster a strong, united and spiritually grounded
                                Muslim community in the Northern Suburbs of Cape Town.
                              </p>
                            ),
                          },
                          {
                            id: 'structure',
                            title: 'Structure',
                            content: (
                              <p className="text-muted mb-0">
                                The Panel consists of committed members representing a balanced and inclusive approach to community
                                service. We work together with the Executive Committee and Trustees, ensuring alignment and
                                collaboration in all operational and religious affairs according to the Quraan and Sunnah.
                              </p>
                            ),
                          },
                          {
                            id: 'services',
                            title: 'Services Offered',
                            content: (
                              <>
                                <p className="text-muted mb-2">All services are rendered free of charge. Our offerings include:</p>
                                <ul className="text-muted mb-0">
                                  <li>Counselling and therapy for individuals, couples and families</li>
                                  <li>Religious classes and courses</li>
                                  <li>Name-giving ceremonies (Tasmiyah)</li>
                                  <li>Nikaah (marriage services)</li>
                                  <li>Janazah (funeral rites)</li>
                                  <li>Islamic wills and inheritance guidance</li>
                                  <li>Mediation and conflict resolution</li>
                                  <li>General religious guidance</li>
                                  <li>Youth programmes and activities</li>
                                  <li>New Muslim support and courses</li>
                                </ul>
                              </>
                            ),
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              )}
              {byType.committee.length > 0 && (
                <div className="mb-5">
                  <h2 className="text-center mb-4 section-subtitle">Our Committee</h2>
                  <div className="row g-4">
                    {byType.committee.map(renderMemberCard)}
                  </div>
                </div>
              )}
              {byType.admin.length > 0 && (
                <div className="mb-5">
                  <h2 className="text-center mb-4 section-subtitle">Admin</h2>
                  <div className="row g-4">
                    {byType.admin.map(renderMemberCard)}
                  </div>
                </div>
              )}
              {byType.trustees.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-center mb-3 h5 text-primary">Trustees</h3>
                  <div className="row g-3 justify-content-center">
                    {byType.trustees.map((m) => (
                      <div key={m.id} className="col-auto">
                        <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 trustee-badge">
                          {m.firstName} {m.lastName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {byType.staff.length > 0 && (
                <div className="mb-5">
                  <h2 className="text-center mb-4 section-subtitle">Staff</h2>
                  <div className="row g-4">
                    {byType.staff.map(renderMemberCard)}
                  </div>
                </div>
              )}
            </section>
          )}

          <section id="constitution" className="mb-5">
            <h2 className="text-center mb-4 section-subtitle">Our Constitution</h2>
            <p className="text-center text-muted mb-4 mx-auto" style={{ maxWidth: '60ch' }}>
              View the constitution of Bothasig Muslim Cultural Society below.
            </p>
            <div className="constitution-pdf-wrapper rounded border bg-light">
              <Worker workerUrl={PDF_WORKER_URL}>
                <Viewer
                  fileUrl={CONSTITUTION_PDF_URL}
                  scrollMode={ScrollMode.Vertical}
                  defaultScale={pdfDefaultScale}
                />
              </Worker>
            </div>
          </section>

          <div className="text-center mt-5">
            <p className="lead mb-3">We'd love to hear from you! Whether you have questions, ideas, or want to get involved, your voice matters.</p>
            <Link to="/contact" className="btn btn-primary btn-lg rounded-pill">
              Contact us
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default AboutPage;
